<template>
  <v-app>
    <v-main>
      <v-container v-if="userStore.loggedIn" class="py-8 px-6" fluid>
        <div class="d-flex align-center mb-5">
          <h1 class="text-h5">Videos</h1>
          <v-spacer />
          <ModalVideoUpload>
            <template v-slot:activator="{ on, attrs, canUpload }">
              <v-btn color="primary" :disabled="!canUpload" v-bind="attrs" v-on="on">
                <v-icon left>mdi-upload</v-icon> Upload video
              </v-btn>
            </template>
          </ModalVideoUpload>
        </div>
        <v-row align="center" class="mb-2">
          <v-col cols="12" md="5">
            <v-text-field v-model="searchInput" label="Search videos by name or ID" prepend-inner-icon="mdi-magnify"
              outlined dense clearable hide-details maxlength="256" @input="queueSearch" />
          </v-col>
          <v-col cols="7" md="4">
            <v-select :value="videoStore.listOptions.sort" :items="sortOptions" label="Sort by"
              outlined dense hide-details @change="changeOptions({ sort: $event })" />
          </v-col>
          <v-col cols="5" md="2">
            <v-select :value="videoStore.listOptions.pageSize" :items="[12, 24, 48]" label="Per page"
              outlined dense hide-details @change="changeOptions({ pageSize: $event })" />
          </v-col>
          <v-col cols="auto" md="1">
            <v-btn icon :loading="videoStore.listLoading" title="Refresh videos" aria-label="Refresh videos" @click="refresh()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-alert v-if="videoStore.listError" type="error" outlined role="alert">
          {{ videoStore.listError }}
          <v-btn text small @click="refresh()">Retry</v-btn>
        </v-alert>
        <div v-if="videos.length" class="d-flex flex-wrap align-center mb-3">
          <span class="text-body-2" aria-live="polite">{{ rangeLabel }}</span>
          <v-spacer />
          <v-btn text small @click="selectPage">Select this page</v-btn>
          <v-btn v-if="videoStore.videoSelected.length" text small @click="videoStore.clearSelected()">
            Clear selection ({{ videoStore.videoSelected.length }})
          </v-btn>
        </div>
        <p v-if="videoStore.videoSelected.length" class="text-caption grey--text">
          Selection applies to this page. Changing pages, search, or sorting clears it.
        </p>
        <v-progress-linear v-if="videoStore.listLoading" indeterminate class="mb-3" aria-label="Loading videos" />
        <div v-if="videoStore.listLoading && !videos.length" class="video-library-grid">
          <v-skeleton-loader v-for="index in 6" :key="index" type="article, actions" />
        </div>
        <v-sheet v-else-if="!videos.length && !videoStore.listError" class="pa-10 text-center" rounded outlined>
          <v-icon size="44" class="mb-3">mdi-video-outline</v-icon>
          <h2 class="text-h6">{{ videoStore.listOptions.search ? 'No matching videos' : 'No videos yet' }}</h2>
          <p>{{ videoStore.listOptions.search ? 'Try a different name or video ID.' : 'Upload a video to get started.' }}</p>
          <v-btn v-if="videoStore.listOptions.search" text color="primary" @click="clearSearch">Clear search</v-btn>
        </v-sheet>
        <div v-else class="video-library-grid" :aria-busy="videoStore.listLoading ? 'true' : 'false'">
          <v-card
            elevation="2"
            v-for="item in videos"
            :key="item.id"
          >
            <v-card-title class="video-overview-title" :title="item.name">{{
              item.name
            }}</v-card-title>
            <v-card-text>
              <div class="video-library-id">Video ID: {{ item.id }}</div>
              <div>
                Length:
                {{ item.duration == null ? 'Unknown' : get_display_time(item.duration) || '0 sec' }}
              </div>
              <div>Uploaded: {{ item.date.slice(0, 10) }}</div>
              <div>Timelines: {{ item.num_timelines }}</div>

              <v-card-actions class="px-0">
                <v-btn outlined small @click="showVideo(item.id)">
                  <v-icon>{{ "mdi-movie-search-outline" }}</v-icon> Analyse
                </v-btn>
                <ModalVideoRename :video="item.id">
                  <template v-slot:activator="on">
                    <v-btn icon :disabled="videoStore.isLoading" :aria-label="'Rename ' + item.name" title="Rename video" v-on="on">
                      <v-icon>{{ "mdi-pencil" }}</v-icon>
                    </v-btn>
                  </template>
                </ModalVideoRename>
                <v-btn color="red" icon :disabled="videoStore.isLoading" :aria-label="'Delete ' + item.name" title="Delete video" @click="deleteVideo(item.id)">
                  <v-icon>{{ "mdi-trash-can-outline" }}</v-icon>
                </v-btn>
                <v-spacer />
                <v-checkbox
                  :input-value="videoStore.videoSelected.includes(item.id)"
                  color="primary"
                  class="mt-0 pt-0"
                  hide-details
                  :aria-label="'Select ' + item.name"
                  @change="videoStore.toggleSelected(item.id)"
                ></v-checkbox>
              </v-card-actions>
              <v-btn text small color="primary" class="px-0" @click="visualize(item.id)">
                <v-icon left small>mdi-play-box-outline</v-icon>Visualize
              </v-btn>
            </v-card-text>
            <v-progress-linear
              v-if="item.progress && item.progress.total"
              :value="Math.round(item.progress.completed * 100 / item.progress.total)"
              :color="item.progress.active ? 'primary' : 'grey'"
              :aria-label="item.progress.completed + ' of ' + item.progress.total + ' analyses finished'"
              :title="item.progress.active + ' active analyses'"
            ></v-progress-linear>
          </v-card>
        </div>
        <v-pagination v-if="videoStore.listPages > 1" class="mt-6" :value="videoStore.listOptions.page"
          :length="videoStore.listPages" :total-visible="7" aria-label="Video pages" @input="changePage" />
        <ModalVisualizationExport v-if="visualizationVideoId" v-model="showVisualization" :video-id="visualizationVideoId" />
      </v-container>
      <v-container v-else>
        <v-col justify="space-around">
          <v-card class="welcome pa-5">
            <v-card-title>
              <h1 class="text-h2">{{ $t("welcome.title") }}</h1>
            </v-card-title>

            <v-card-text>
              <p v-html="$t('welcome.text')"></p>
              <h2 class="text-h5 mb-2">{{ $t("welcome.demo_title") }}</h2>
              <p>
                <video id="welcome-video" controls>
                  <source
                    src="https://tib.eu/cloud/s/sMmqWqWYict3Zpb/download/TIB-AV-A_Einfuehrung_2.mp4"
                    type="video/mp4"
                  />
                </video>
              </p>
              <h2 class="text-h5 mb-1 mt-4">{{ $t("welcome.login_title") }}</h2>
              <p v-html="$t('welcome.login_text')"></p>
              <h2 class="text-h5 mb-1 mt-4">
                {{ $t("welcome.format_title") }}
              </h2>
              <p v-html="$t('welcome.format_text')"></p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import router from "../router";
import ModalVideoUpload from "@/components/ModalVideoUpload.vue";
import ModalVideoRename from "@/components/ModalVideoRename.vue";
import ModalVisualizationExport from "@/components/ModalVisualizationExport.vue";
import TimeMixin from "../mixins/time";
import { mapStores } from "pinia";
import { useVideoStore } from "@/store/video.js";
import { useUserStore } from "@/store/user.js";
import { usePluginRunStore } from "@/store/plugin_run.js";

export default {
  mixins: [TimeMixin],
  data() {
    return {
      searchInput: "",
      searchTimer: null,
      pollTimer: null,
      active: false,
      visualizationVideoId: null,
      showVisualization: false,
      sortOptions: [
        { text: "Newest first", value: "-date" },
        { text: "Oldest first", value: "date" },
        { text: "Name A–Z", value: "name" },
        { text: "Name Z–A", value: "-name" },
        { text: "Longest first", value: "-duration" },
        { text: "Shortest first", value: "duration" },
      ],
    };
  },
  mounted() {
    this.active = true;
    this.searchInput = this.videoStore.listOptions.search;
    document.addEventListener("visibilitychange", this.visibilityChanged);
    this.refresh();
  },
  beforeDestroy() {
    this.active = false;
    clearTimeout(this.searchTimer);
    clearTimeout(this.pollTimer);
    document.removeEventListener("visibilitychange", this.visibilityChanged);
    this.videoStore.cancelListRequest();
    this.videoStore.clearSelected();
  },
  methods: {
    visualize(videoId) {
      this.visualizationVideoId = videoId;
      this.showVisualization = true;
    },
    selectPage() {
      this.videoStore.videoSelected = this.videos.map(video => video.id);
    },
    async deleteVideo(video_id) {
      try {
        await this.videoStore.delete(video_id);
      } catch (error) {
        this.videoStore.listError = "Could not delete the video. Please try again.";
      }
    },
    showVideo(video_id) {
      router.push({ path: `/videoanalysis/${video_id}` });
    },
    queueSearch() {
      clearTimeout(this.searchTimer);
      clearTimeout(this.pollTimer);
      this.videoStore.cancelListRequest();
      this.searchTimer = setTimeout(() => this.changeOptions({}), 350);
    },
    clearSearch() {
      this.searchInput = "";
      this.changeOptions({});
    },
    changeOptions(options) {
      clearTimeout(this.searchTimer);
      this.refresh(false, { ...options, page: 1, search: (this.searchInput || "").trim() });
    },
    changePage(page) {
      this.refresh(false, { page });
    },
    async refresh(silent = false, options = {}) {
      clearTimeout(this.pollTimer);
      if (!this.active || !this.userStore.loggedIn) return;
      const response = await this.videoStore.fetchPage(options, { silent });
      if (response && this.active && !document.hidden && this.videos.some(video => video.progress.active > 0)) {
        this.pollTimer = setTimeout(() => this.refresh(true), 5000);
      }
    },
    visibilityChanged() {
      clearTimeout(this.pollTimer);
      if (!document.hidden) this.refresh(true);
    },
  },
  computed: {
    videos() {
      return this.videoStore.all;
    },
    rangeLabel() {
      const first = (this.videoStore.listOptions.page - 1) * this.videoStore.listOptions.pageSize + 1;
      return `${first}–${first + this.videos.length - 1} of ${this.videoStore.listTotal} videos`;
    },
    ...mapStores(
      useVideoStore,
      usePluginRunStore,
      useUserStore
    ),
  },
  watch: {
    "userStore.loggedIn"(value) {
      if (value) {
        this.refresh();
      } else {
        clearTimeout(this.searchTimer);
        clearTimeout(this.pollTimer);
        this.videoStore.clearStore();
        this.searchInput = "";
        this.showVisualization = false;
        this.visualizationVideoId = null;
      }
    },
    "videoStore.listRevision"() {
      this.refresh(true);
    },
    "pluginRunStore.submissionRevision"() {
      this.refresh(true);
    },
  },
  components: {
    ModalVideoUpload,
    ModalVideoRename,
    ModalVisualizationExport,
  },
};
</script>

<style>
.video-overview-title {
  display: block !important;
  white-space: nowrap;
  overflow: hidden;
  width: 100%;
  text-overflow: ellipsis;
}

.video-library-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 310px), 1fr));
  gap: 20px;
}
.video-library-id {
  overflow-wrap: anywhere;
}
#welcome-video {
  margin-left: auto;
  margin-right: auto;
  display: block;
  border-style: outset;
  border-color: black;
  max-width: 800px;
}
.center-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}
</style>
